import React, { useMemo } from "react";
import {
    Box,
    Button,
    Chip,
    FormControl,
    IconButton,
    InputLabel,
    ListSubheader,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadIcon from "@mui/icons-material/Download";
import TuneIcon from "@mui/icons-material/Tune";
import { Link as RouterLink } from "react-router-dom";
import {
    computeQuoteTotals,
    formatMoney,
    lineExtension,
    newEstimateLineId,
} from "./quotePricingMath";
import {
    RATE_CATEGORY_LABELS,
    RATE_CATEGORY_ORDER,
    QTY_HOUR_STEPS,
    groupPresetsByCategory,
    groupRatesByCategory,
    isHourlyOnlyRate,
} from "./pricingCatalogMeta";
import { formatHours, groupQuoteOwn, rollupPlannerQuotes } from "./plannerQuoteRates";
import { robotCostFromWixComponents } from "./wixJobQuote";
import CustomerQuotePackagesView from "./CustomerQuotePackagesView";
import InternalQuoteEconomicsView from "./InternalQuoteEconomicsView";

function unitChipLabel(unit) {
    const u = String(unit || "hour");
    if (u === "day") return "day";
    if (u === "flat") return "flat";
    return "hr";
}

function ComponentQuoteSection({ components, currency, totals }) {
    if (!components?.length) return null;
    const customerSupplied = components.reduce((sum, component) => sum + (Number(component.customerPurchases) || 0), 0);
    const roughProjectValue = (Number(totals?.customer_total_exact) || 0) + customerSupplied;
    const robotCost = robotCostFromWixComponents(components);
    const projectValueLessRobot = roughProjectValue - robotCost;
    const flatten = (node, depth = 0, rows = []) => {
        rows.push({ node, depth });
        (node.children || []).forEach((child) => flatten(child, depth + 1, rows));
        return rows;
    };
    return (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 1.75 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>
                Internal component roll-up
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
                Hours, rates, and PBR buy costs stay internal. The customer presentation above shows package totals only.
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 1.25 }}>
                <Typography variant="body2">
                    PBR billable total: <strong>{formatMoney(totals?.customer_total_exact, currency)}</strong>
                </Typography>
                <Typography variant="body2">
                    Customer-supplied value: <strong>{formatMoney(customerSupplied, currency)}</strong>
                </Typography>
                <Typography variant="body2">
                    Rough project value: <strong>{formatMoney(roughProjectValue, currency)}</strong>
                </Typography>
                <Typography variant="body2">
                    Robot cost: <strong>{formatMoney(robotCost, currency)}</strong>
                </Typography>
                <Typography variant="body2">
                    Project value less robot cost: <strong>{formatMoney(projectValueLessRobot, currency)}</strong>
                </Typography>
            </Stack>
            <Stack spacing={1.25}>
                {components.map((component) => {
                    const variance = component.target == null ? null : component.variance;
                    return (
                        <Paper key={component.id} variant="outlined" sx={{ overflow: "hidden" }}>
                            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="space-between" sx={{ p: 1.25 }}>
                                <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 800 }}>{component.title}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {formatHours(component.laborHours) || "No labor"} · labor {formatMoney(component.laborCost, currency)} · PBR charge {formatMoney(component.purchasesCustomer, currency)} ({formatMoney(component.purchases, currency)} cost) · customer-supplied {formatMoney(component.customerPurchases, currency)}
                                        {component.contingencyPct
                                            ? ` · ${component.contingencyPct}% contingency (+${formatMoney(component.riskLoadedTotal - component.builtUp, currency)}) · risk-loaded ${formatMoney(component.riskLoadedTotal, currency)}`
                                            : ""}
                                    </Typography>
                                </Box>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Box sx={{ textAlign: "right" }}>
                                        <Typography variant="body2" sx={{ fontWeight: 800 }}>Rough {formatMoney(component.roughTotal, currency)}</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            PBR {formatMoney(component.builtUp, currency)}
                                            {component.contingencyPct
                                                ? ` · +${formatMoney(component.riskLoadedTotal - component.builtUp, currency)} risk · loaded ${formatMoney(component.riskLoadedTotal, currency)}`
                                                : ""}
                                        </Typography>
                                    </Box>
                                    {component.target != null ? (
                                        <Chip
                                            size="small"
                                            color={variance >= 0 ? "success" : "error"}
                                            variant="outlined"
                                            label={`${formatMoney(component.target, currency)} target · ${variance >= 0 ? "under" : "over"} ${formatMoney(Math.abs(variance), currency)}`}
                                        />
                                    ) : null}
                                </Stack>
                            </Stack>
                            <TableContainer sx={{ borderTop: 1, borderColor: "divider", overflowX: "auto" }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ "& th": { fontWeight: 800, whiteSpace: "nowrap" } }}>
                                            <TableCell>Child line</TableCell>
                                            <TableCell>Type / rate</TableCell>
                                            <TableCell align="right">Hours</TableCell>
                                            <TableCell align="right">Labor</TableCell>
                                            <TableCell align="right">PBR cost</TableCell>
                                            <TableCell align="right">PBR charge</TableCell>
                                            <TableCell align="right">Customer-supplied</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {flatten(component).slice(1).map(({ node, depth }) => (
                                            <TableRow key={node.id}>
                                                <TableCell sx={{ pl: 1.5 + depth * 2 }}>
                                                    <Typography variant="body2" sx={{ fontWeight: node.lineKind === "component" ? 700 : 500 }}>
                                                        {node.title}
                                                        {node.hideFromCustomer || node.customerVisible === false ? " · internal only" : ""}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="caption">
                                                        {node.lineKind || "component"}{node.quoteHourlyRate ? ` · ${node.quoteRateLabel} $${node.quoteHourlyRate}/hr` : ""}
                                                    </Typography>
                                                    {node.lineKind === "purchase" && node.purchaseTreatment !== "pbr" ? (
                                                        <Typography variant="caption" color="text.secondary" display="block">
                                                            {node.purchaseTreatment === "customer" ? "Customer purchase · excluded" : "Note only · excluded"}
                                                        </Typography>
                                                    ) : null}
                                                </TableCell>
                                                <TableCell align="right">{node.ownHours ? formatHours(node.ownHours) : "—"}</TableCell>
                                                <TableCell align="right">{node.ownCost ? formatMoney(node.ownCost, currency) : "—"}</TableCell>
                                                <TableCell align="right">{node.ownPurchases ? formatMoney(node.ownPurchases, currency) : "—"}</TableCell>
                                                <TableCell align="right">{node.ownPurchasesCustomer ? formatMoney(node.ownPurchasesCustomer, currency) : "—"}</TableCell>
                                                <TableCell align="right">{node.ownCustomerPurchases ? formatMoney(node.ownCustomerPurchases, currency) : "—"}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    );
                })}
            </Stack>
        </Paper>
    );
}

export default function QuoteEstimatePanel({
    currency,
    settings,
    rates,
    presets,
    estimateLines,
    setEstimateLines,
    materialCost,
    onMaterialCostChange,
    customerQuoteOverride,
    onCustomerQuoteOverrideChange,
    onCopyCostBreakdown,
    onCopyCustomerBrief,
    onExportInternalPdf,
    onExportCustomerPdf,
    pdfExporting,
    wixComponents,
    wixJob,
    plannerGroups,
    activeGroupFilter,
    onActiveGroupFilterChange,
}) {
    const totals = useMemo(
        () => computeQuoteTotals({ material_cost_at_purchase: materialCost, lines: estimateLines, settings }),
        [materialCost, estimateLines, settings]
    );

    const ratesByKey = useMemo(() => {
        const map = {};
        (rates || []).forEach((r) => {
            map[r.rate_key] = r;
        });
        return map;
    }, [rates]);

    const rateChoices = useMemo(() => [...(rates || [])].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)), [
        rates,
    ]);

    const ratesByCategory = useMemo(() => groupRatesByCategory(rateChoices), [rateChoices]);
    const presetsByCategory = useMemo(() => groupPresetsByCategory(presets), [presets]);

    const addBlankLine = (groupId = null) => {
        setEstimateLines((rows) => [
            ...rows,
            {
                id: newEstimateLineId(),
                label: "",
                rate_key: null,
                qty: 8,
                unit: "hour",
                bill_rate: 0,
                group_id: groupId,
            },
        ]);
    };

    const addPresetLine = (preset, groupId = null) => {
        const r = ratesByKey[preset.rate_key];
        if (!r) return;
        const unit = isHourlyOnlyRate(preset.rate_key)
            ? "hour"
            : String(preset.unit || r.unit || "hour");
        setEstimateLines((rows) => [
            ...rows,
            {
                id: newEstimateLineId(),
                label: preset.label || r.label || "Line item",
                rate_key: preset.rate_key,
                qty: Number(preset.default_qty) || 1,
                unit,
                bill_rate: Number(r.suggested_rate) || 0,
                group_id: groupId,
            },
        ]);
    };

    const enabledGroups = useMemo(
        () => (plannerGroups || []).filter((g) => g.enabled !== false),
        [plannerGroups]
    );

    const plannerQuote = useMemo(() => rollupPlannerQuotes(plannerGroups), [plannerGroups]);

    const visibleLines = useMemo(() => {
        if (!activeGroupFilter) return estimateLines;
        return estimateLines.filter((L) => L.group_id === activeGroupFilter);
    }, [estimateLines, activeGroupFilter]);

    const patchLine = (id, patch) => {
        setEstimateLines((rows) => rows.map((L) => (L.id === id ? { ...L, ...patch } : L)));
    };

    const removeLine = (id) => setEstimateLines((rows) => rows.filter((L) => L.id !== id));

    const applyRateCatalog = (id, selectedKey) => {
        if (!selectedKey || selectedKey === "__custom") {
            return patchLine(id, { rate_key: null });
        }
        const r = ratesByKey[selectedKey];
        if (!r) return;
        const unit = isHourlyOnlyRate(r.rate_key) ? "hour" : r.unit;
        patchLine(id, { rate_key: r.rate_key, unit, bill_rate: r.suggested_rate });
    };

    const cur = currency || settings?.currency || "USD";

    return (
        <Paper elevation={0} variant="outlined" sx={{ p: { xs: 2, sm: 2.25 } }}>
            <Stack spacing={1.5} sx={{ mb: 1.75 }}>
                <Box sx={{ maxWidth: 900 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 850, mt: -0.5 }}>
                        Fast estimate builder
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Model hours against your standardized rates. App development is remote-only (no travel). Onsite work bills
                        by the hour — use 4 / 8 / 40 hr presets or type exact hours.
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
                        Tip — split remote engineering, onsite hours, and app dev into separate rows so you can adjust each on a call.
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<TuneIcon />}
                        component={RouterLink}
                        to="/pricing"
                        sx={{ whiteSpace: "nowrap" }}
                    >
                        Quote Settings
                    </Button>
                    <Tooltip title="Copy customer-facing recap with package totals">
                        <span>
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<ContentCopyIcon />}
                                onClick={() => onCopyCustomerBrief?.(totals)}
                            >
                                Copy customer brief
                            </Button>
                        </span>
                    </Tooltip>
                    <Tooltip title="Copy CFO-style workbook with buckets & math">
                        <span>
                            <Button
                                size="small"
                                variant="outlined"
                                color="secondary"
                                startIcon={<ContentCopyIcon />}
                                onClick={() => onCopyCostBreakdown?.(totals)}
                            >
                                Copy cost detail
                            </Button>
                        </span>
                    </Tooltip>
                    <Tooltip title="Download full internal-use PDF report">
                        <span>
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<DownloadIcon />}
                                onClick={() => onExportInternalPdf?.()}
                                disabled={pdfExporting}
                            >
                                {pdfExporting ? "Generating…" : "Internal PDF"}
                            </Button>
                        </span>
                    </Tooltip>
                    <Tooltip title="Download polished customer-facing quote">
                        <span>
                            <Button
                                size="small"
                                variant="contained"
                                color="primary"
                                startIcon={<DownloadIcon />}
                                onClick={() => onExportCustomerPdf?.()}
                                disabled={pdfExporting}
                            >
                                {pdfExporting ? "Generating…" : "Customer PDF"}
                            </Button>
                        </span>
                    </Tooltip>
                </Stack>
            </Stack>

            <CustomerQuotePackagesView
                components={wixComponents}
                currency={cur}
                title="Customer presentation"
                sx={{ mb: 1.75 }}
            />

            <InternalQuoteEconomicsView
                components={wixComponents}
                currency={cur}
                customerTotal={totals?.customer_total_exact}
                actualHours={wixJob?.actualHours}
                extraMaterialCost={wixJob?.extraMaterialCost}
                sx={{ mb: 1.75 }}
            />

            {enabledGroups.length > 0 && plannerQuote.laborHours > 0 ? (
                <Paper variant="outlined" sx={{ p: 1.5, mb: 1.75 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.75 }}>
                        Planner labor × rate
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {formatHours(plannerQuote.laborHours)} · labor {formatMoney(plannerQuote.cost, cur)} · purchases{" "}
                        {formatMoney(plannerQuote.purchases, cur)} · budget {formatMoney(plannerQuote.total, cur)}
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                        {enabledGroups
                            .filter((g) => groupQuoteOwn(g).laborHours > 0)
                            .map((g) => {
                                const q = groupQuoteOwn(g);
                                return (
                                    <Chip
                                        key={g.id}
                                        size="small"
                                        label={`${g.label}: ${formatHours(q.laborHours)} @ $${q.profile.hourlyRate}/hr = ${formatMoney(q.cost, cur)}`}
                                    />
                                );
                            })}
                    </Box>
                </Paper>
            ) : null}

            <ComponentQuoteSection components={wixComponents} currency={cur} totals={totals} />

            <Stack spacing={1.5} sx={{ mb: 1.75 }}>
                <Typography variant="body2" color="text.secondary">
                    Quick-insert presets:
                </Typography>
                {RATE_CATEGORY_ORDER.map((cat) => {
                    const catPresets = presetsByCategory[cat] || [];
                    if (catPresets.length === 0) return null;
                    return (
                        <Box key={cat}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, display: "block", mb: 0.5 }}>
                                {RATE_CATEGORY_LABELS[cat]}
                            </Typography>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                {catPresets.map((p) => (
                                    <Chip key={p.preset_key} label={p.label} size="small" onClick={() => addPresetLine(p)} />
                                ))}
                            </Box>
                        </Box>
                    );
                })}
                {(presets || []).length === 0 ? (
                    <Typography variant="caption" color="text.secondary">
                        No presets configured — seed them via Quote Settings → save.
                    </Typography>
                ) : null}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                    {enabledGroups.length > 0 ? (
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                            <InputLabel>Filter by group</InputLabel>
                            <Select
                                label="Filter by group"
                                value={activeGroupFilter || ""}
                                onChange={(e) => onActiveGroupFilterChange?.(e.target.value || null)}
                            >
                                <MenuItem value="">All groups</MenuItem>
                                {enabledGroups.map((g) => (
                                    <MenuItem key={g.id} value={g.id}>{g.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    ) : null}
                    <Button size="small" variant="text" startIcon={<AddIcon />} onClick={() => addBlankLine(activeGroupFilter || null)}>
                        Add custom line
                    </Button>
                </Box>
            </Stack>

            <GridPairRow
                sx={{ mb: 1 }}
                materialCost={materialCost}
                onMaterialCostChange={onMaterialCostChange}
                customerQuoteOverride={customerQuoteOverride}
                onCustomerQuoteOverrideChange={onCustomerQuoteOverrideChange}
            />

            <TableContainer sx={{ border: 1, borderColor: "divider", borderRadius: 1, overflowX: "auto", mb: 1.75 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ "& th": { fontWeight: 800, whiteSpace: "nowrap" } }}>
                            <TableCell>Scope / task</TableCell>
                            <TableCell>Planner group</TableCell>
                            <TableCell>Rate bucket</TableCell>
                            <TableCell>Unit</TableCell>
                            <TableCell align="right">Qty</TableCell>
                            <TableCell align="right">$/unit</TableCell>
                            <TableCell align="right">Extend</TableCell>
                            <TableCell align="right" width={54} />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {visibleLines.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={8}>
                                    <Typography variant="body2" color="text.secondary">
                                        No estimate lines yet. Tap a preset chip or add a custom line — each row snapshots the price
                                        you typed so you remember what told the customer.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            visibleLines.map((L) => {
                                const sel = L.rate_key && ratesByKey[L.rate_key] ? L.rate_key : "__custom";
                                const extended = lineExtension(L);
                                return (
                                    <TableRow key={L.id}>
                                        <TableCell sx={{ minWidth: 220 }}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                value={L.label}
                                                placeholder="CAM prove-out, plc checkout, weld fixturing..."
                                                onChange={(e) => patchLine(L.id, { label: e.target.value })}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ minWidth: 160 }}>
                                            {enabledGroups.length > 0 ? (
                                                <Select
                                                    size="small"
                                                    fullWidth
                                                    displayEmpty
                                                    value={L.group_id || ""}
                                                    onChange={(e) => patchLine(L.id, { group_id: e.target.value || null })}
                                                >
                                                    <MenuItem value=""><em>Unassigned</em></MenuItem>
                                                    {enabledGroups.map((g) => (
                                                        <MenuItem key={g.id} value={g.id}>{g.label}</MenuItem>
                                                    ))}
                                                </Select>
                                            ) : (
                                                <Typography variant="caption" color="text.secondary">—</Typography>
                                            )}
                                        </TableCell>
                                        <TableCell sx={{ minWidth: 200 }}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel shrink>Pick catalog</InputLabel>
                                                <Select
                                                    label="Pick catalog"
                                                    notched
                                                    value={sel}
                                                    onChange={(e) => applyRateCatalog(L.id, e.target.value)}
                                                    displayEmpty
                                                >
                                                    <MenuItem value="__custom">
                                                        <em>Custom / manual pricing</em>
                                                    </MenuItem>
                                                    {RATE_CATEGORY_ORDER.map((cat) => {
                                                        const catRates = ratesByCategory[cat] || [];
                                                        if (catRates.length === 0) return null;
                                                        return [
                                                            <ListSubheader key={`hdr-${cat}`}>{RATE_CATEGORY_LABELS[cat]}</ListSubheader>,
                                                            ...catRates.map((r) => (
                                                                <MenuItem key={r.rate_key} value={r.rate_key}>
                                                                    {r.label}
                                                                </MenuItem>
                                                            )),
                                                        ];
                                                    })}
                                                </Select>
                                            </FormControl>
                                            {L.rate_key && ratesByKey[L.rate_key]?.description ? (
                                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                                    {ratesByKey[L.rate_key].description}
                                                </Typography>
                                            ) : null}
                                        </TableCell>
                                        <TableCell sx={{ minWidth: 120 }}>
                                            <Select
                                                size="small"
                                                fullWidth
                                                value={L.unit || "hour"}
                                                onChange={(e) => patchLine(L.id, { unit: e.target.value })}
                                            >
                                                <MenuItem value="hour">hours</MenuItem>
                                                {!isHourlyOnlyRate(L.rate_key) ? (
                                                    <MenuItem value="day">days</MenuItem>
                                                ) : null}
                                                <MenuItem value="flat">flat</MenuItem>
                                            </Select>
                                        </TableCell>
                                        <TableCell align="right" sx={{ width: 168 }}>
                                            <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
                                                {(L.unit || "hour") === "hour" ? (
                                                    <Stack direction="row" spacing={0.25} sx={{ flexWrap: "wrap", maxWidth: 88 }}>
                                                        {QTY_HOUR_STEPS.map((step) => (
                                                            <Chip
                                                                key={step}
                                                                label={`+${step}`}
                                                                size="small"
                                                                variant="outlined"
                                                                onClick={() => {
                                                                    const base = Number(L.qty) || 0;
                                                                    patchLine(L.id, { qty: Math.round((base + step) * 4) / 4 });
                                                                }}
                                                                sx={{ height: 22, fontSize: "0.65rem" }}
                                                            />
                                                        ))}
                                                    </Stack>
                                                ) : null}
                                                <TextField
                                                    size="small"
                                                    value={String(L.qty ?? "")}
                                                    onChange={(e) => {
                                                        const raw = e.target.value;
                                                        if (raw === "") return patchLine(L.id, { qty: "" });
                                                        const v = Number(raw);
                                                        patchLine(L.id, { qty: Number.isFinite(v) ? v : 0 });
                                                    }}
                                                    inputProps={{
                                                        inputMode: "decimal",
                                                        step: (L.unit || "hour") === "hour" ? 0.25 : 1,
                                                        min: 0,
                                                        style: { textAlign: "right", width: 56 },
                                                    }}
                                                />
                                            </Stack>
                                        </TableCell>
                                        <TableCell align="right" sx={{ width: 130 }}>
                                            <TextField
                                                size="small"
                                                value={String(L.bill_rate ?? "")}
                                                onChange={(e) => {
                                                    const raw = e.target.value;
                                                    if (raw === "") return patchLine(L.id, { bill_rate: "" });
                                                    const v = Number(raw);
                                                    patchLine(L.id, { bill_rate: Number.isFinite(v) ? v : 0 });
                                                }}
                                                inputProps={{ inputMode: "decimal", style: { textAlign: "right" } }}
                                            />
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                                            <Tooltip title={`${L.qty || 0} ${unitChipLabel(L.unit)} × ${L.bill_rate || 0} ${cur}`}>
                                                <span>{formatMoney(extended, cur)}</span>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton size="small" aria-label="Delete line" onClick={() => removeLine(L.id)}>
                                                <DeleteOutlineIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ alignItems: { sm: "flex-start" } }}>
                <Paper variant="outlined" sx={{ flex: 1, p: 1.75 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                        Model roll-up ({cur})
                    </Typography>
                    <TotalsRow label="Services / scoped labor extension" amount={totals.services_subtotal} cur={cur} />
                    <TotalsRow
                        label={`Materials (${totals.materials_markup_percent.toFixed(2)}% markup on your cost)`}
                        amount={totals.materials_customer}
                        cur={cur}
                        hint={`Cost basis ${formatMoney(totals.materials_cost_basis, cur)}`}
                    />
                    <TotalsRow
                        label={`Contingency / risk (${totals.contingency_percent.toFixed(2)}%)`}
                        amount={totals.contingency_amount}
                        cur={cur}
                    />
                    {totals.tax_percent > 0 ? (
                        <TotalsRow label={`Tax (${totals.tax_percent.toFixed(2)}%)`} amount={totals.tax_amount} cur={cur} />
                    ) : null}

                    <Box sx={{ mt: 1.25 }} />
                    <TotalsRow label="Exact model total (pre rounding)" amount={totals.customer_total_exact} cur={cur} emphasis />
                    <TotalsRow label="Rounded customer ballpark (for phone quotes)" amount={totals.customer_total_rounded} cur={cur} strong />
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                        Rounding mode: {String(totals.rounding_mode).replace(/_/g, " ")} — tweak under Quote Settings anytime.
                    </Typography>
                    {customerQuoteOverride !== "" &&
                        Number.isFinite(Number(customerQuoteOverride)) &&
                        Number(customerQuoteOverride) > 0 ? (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                            Voice-deal recorder:{" "}
                            <Box component="span" sx={{ fontWeight: 800 }}>
                                you told them {formatMoney(Number(customerQuoteOverride), cur)}
                            </Box>
                        </Typography>
                    ) : null}
                </Paper>
                <Paper variant="outlined" sx={{ flex: 1, p: 1.75 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.75 }}>
                        How this stays accurate
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
                        Start from presets. App dev and onsite buckets stay hourly — stack 4 / 8 / 40 hr blocks or use the +qty
                        chips on each row. Remote shop work can still use day or flat units when it makes sense.
                    </Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.75 }}>
                        On the phone playbook
                    </Typography>
                    <Typography variant="body2" color="text.secondary" component="ul" sx={{ m: 0, pl: 2.25 }}>
                        <li>Quote the rounded total aloud, caveat &quot;if scope holds&quot; referencing worksheet notes.</li>
                        <li>Immediately log verbal commit in Voice-deal field so legal follow-up aligns.</li>
                        <li>Afterward tune catalog rates only in Quote Settings — per-quote snapshots keep history honest.</li>
                    </Typography>
                </Paper>
            </Stack>
        </Paper>
    );
}

function TotalsRow({ label, amount, cur, emphasis, strong, hint }) {
    const fw = strong ? 900 : emphasis ? 800 : 600;
    return (
        <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="baseline" sx={{ mb: hint ? 0.25 : 0.85 }}>
            <Box>
                <Typography variant="body2" sx={{ fontWeight: fw }}>
                    {label}
                </Typography>
                {hint ? (
                    <Typography variant="caption" color="text.secondary">
                        {hint}
                    </Typography>
                ) : null}
            </Box>
            <Typography variant="body2" sx={{ fontWeight: fw, ml: 1, whiteSpace: "nowrap" }}>
                {formatMoney(amount, cur)}
            </Typography>
        </Stack>
    );
}

function GridPairRow({ sx, materialCost, onMaterialCostChange, customerQuoteOverride, onCustomerQuoteOverrideChange }) {
    return (
        <Stack spacing={2} sx={sx}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                    label="Purchased-material cost basis (your out-of-pocket estimate)"
                    type="number"
                    fullWidth
                    size="small"
                    InputProps={{ inputProps: { min: 0, step: 50 } }}
                    value={Number.isFinite(Number(materialCost)) ? materialCost : 0}
                    onChange={(e) => onMaterialCostChange?.(Math.max(0, Number(e.target.value) || 0))}
                    helperText="Hardware, guarding, peripherals you buy resale — excludes customer-supplied."
                />
                <TextField
                    label='Voice-deal total (what you actually said aloud — optional)'
                    type="number"
                    fullWidth
                    size="small"
                    InputProps={{ inputProps: { min: 0, step: 100 } }}
                    placeholder="Matches verbal ballpark?"
                    value={customerQuoteOverride}
                    onChange={(e) => onCustomerQuoteOverrideChange?.(e.target.value)}
                    helperText="Does not alter math — keeps bookkeeping honest when adrenaline rounds differently."
                />
            </Stack>
        </Stack>
    );
}
